<template>
    <div class="apartement-main-container" v-if="!loading">
        <div class="header-container">
            <ClassicBreadCrumb :title="routeName" />
            <h1>{{ title }}</h1>
            <h2>* {{ $t("vigik.form.obligatoryFields") }}</h2>
        </div>
        <div class="content-container">
            <div class="left-part-container">
                <div class="input-wrapper">
                    <p>{{ $t("apartmentsList.arrayColumns.name") }} *</p>
                    <BasicInput
                        v-model="name"
                        class="input"
                        name="name"
                        maxlength="40"
                    />
                </div>
                <div class="input-wrapper">
                    <p>{{ $t("apartmentsList.createOrEdit.floor") }}</p>
                    <BasicDropdown
                        v-model="floor"
                        class="input"
                        name="floor"
                        :options="floorOptions"
                    />
                </div>
                <div class="input-wrapper">
                    <p>{{ $t("apartmentsList.createOrEdit.letterBox") }}</p>
                    <BasicDropdown
                        v-model="box"
                        class="input"
                        name="box"
                        :options="boxOptions"
                    />
                </div>
                <div class="input-wrapper">
                    <p>{{ $t("centralUnitsList.arrayColumns.type") }}</p>
                    <BasicDropdown
                        v-model="type"
                        class="input"
                        name="type"
                        :options="typeOptions"
                    />
                </div>
                <div class="input-wrapper">
                    <p>{{ $t("agencesList.arrayColumns.comment") }}</p>
                    <BasicInput
                        v-model="comment"
                        class="input"
                        name="comment"
                        maxlength="150"
                        :textarea="true"
                    />
                </div>
                <div class="checkbox-container" v-if="isLocative">
                    <div class="checkbox-wrapper">
                        <BasicInput
                            v-model="locativeChoice"
                            class="checkbox"
                            type="checkbox"
                            name="checkbox"
                        />
                        <p>
                            {{ $t("residencesList.form.locative") }}
                        </p>
                    </div>
                    <div
                        class="display-hours"
                        @click="goTo('LocativeGestion')"
                        v-if="locativeChoice"
                    >
                        <p>{{ $t("apartmentsList.createOrEdit.configure") }}</p>
                    </div>
                </div>
                <div class="checkbox-container" v-if="isLiberal">
                    <div class="checkbox-wrapper">
                        <BasicInput
                            v-model="liberalChoice"
                            class="checkbox"
                            type="checkbox"
                            name="checkbox"
                        />
                        <p>
                            {{
                                $t("apartmentsList.createOrEdit.liberalGestion")
                            }}
                            <i
                                class="fa-sharp fa-regular fa-circle-exclamation"
                                @click="liberalInfos"
                            ></i>
                        </p>
                    </div>
                    <div
                        class="display-hours"
                        @click="goTo('DisplayHours')"
                        v-if="liberalChoice"
                    >
                        <p>
                            {{
                                $t(
                                    "apartmentsList.createOrEdit.changeDisplayHours"
                                )
                            }}
                        </p>
                    </div>
                </div>
                <div class="button-wrapper">
                    <BasicButton
                        color="orange"
                        :disabled="!obligatoryFields"
                        @click="submitAction"
                        >{{ $t("button.register") }}
                    </BasicButton>
                    <BasicButton
                        color="blue"
                        @click="goBack()"
                        v-if="$route.params.action == 'create'"
                        >{{ $t("button.cancel") }}
                    </BasicButton>
                    <BasicButton
                        color="blue"
                        v-if="$route.params.action !== 'create'"
                        @click="eraseApartment()"
                    >
                        {{ $t("button.delete") }}
                    </BasicButton>
                    <BasicButton
                        color="blue"
                        @click="moveToApartment()"
                        v-if="canBeMoved"
                    >
                        {{ $t("button.moveTo") }}
                    </BasicButton>
                </div>
            </div>
            <div class="separator-line" />
            <div class="right-part-container">
                <div class="image-wrapper">
                    <img
                        alt="apartment image"
                        src="@/assets/images/apartement/door.svg"
                    />
                </div>
            </div>
        </div>
    </div>
    <Loader class="loader" v-else />
</template>

<script>
import v1mixin from "@/mixins/v1.js"
import BasicInput from "@/components/basic/BasicInput.vue"
import BasicDropdown from "@/components/basic/BasicDropdown.vue"
import BasicButton from "@/components/basic/BasicButton.vue"
import ClassicBreadCrumb from "@/components/entities/ClassicBreadCrumb.vue"
import Loader from "@/components/basic/Loader.vue"
import {
    createApartment,
    getApartment,
    updateApartment,
    deleteApartment,
} from "@/services/intratone/apartment"
import { getStairwellList } from "@/services/intratone/stairwell"
import { useToast } from "vue-toastification"

const toast = useToast()

export default {
    name: "ApartmentCreateOrEdit",
    components: {
        BasicInput,
        BasicDropdown,
        BasicButton,
        ClassicBreadCrumb,
        Loader,
    },
    data() {
        return {
            loading: false,
            appartmentId: this.$route.params.id,
            name: "",
            floor: {},
            floorOptions: [],
            box: {},
            boxOptions: [],
            type: {},
            typeOptions: [],
            comment: "",
            locativeChoice: false,
            liberalChoice: false,
            liberalRangeId: null,
            initialData: [],
        }
    },
    computed: {
        title() {
            return this.$route.params.action === "edit"
                ? this.$t("apartmentsList.params")
                : this.$t("apartmentsList.create")
        },

        routeName() {
            return this.$route.params.action !== "create"
                ? this.$t("apartmentsList.params")
                : this.$t("apartmentsList.create")
        },

        isLocative() {
            return this.$store.getters["residenceInfos/getLocative"] == 1
        },

        isLiberal() {
            return this.$store.getters["residenceInfos/getLiberal"] == 1
        },

        breadCrumb() {
            return this.$store.getters["breadCrumb/getClassicBreadCrumb"]
        },

        obligatoryFields() {
            if (this.$route.params.action === "create") {
                return this.name
            } else if (this.$route.params.action === "edit") {
                return (
                    (this.initialData.nom !== this.name ||
                        this.initialData.etage != this.floor.value ||
                        this.initialData.bal != this.box.value ||
                        this.initialData.type != this.type.value ||
                        this.initialData.comments !== this.comment ||
                        this.initialData.loc != (this.locativeChoice ? 1 : 0) ||
                        this.initialData.liberale !=
                            (this.liberalChoice ? 1 : 0)) &&
                    this.name
                )
            }

            return false
        },

        isSimplifiedResidence() {
            const store = this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            const residence = store.find(
                (item) => item.routeName === "Residence"
            )

            if (residence) {
                return residence.options?.idCage ? true : false
            } else {
                return false
            }
        },

        stairwellId() {
            const queryStairwellId = this.$route.query.stairwellId
            const store = this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            if (queryStairwellId) return parseInt(queryStairwellId)

            if (store && store.length > 0) {
                for (let i = store.length - 1; i >= 0; i--) {
                    if (store[i].routeName === "Residence") {
                        if (store[i].options && store[i].options.idCage)
                            return store[i].options.idCage
                    }

                    if (store[i].routeName === "Stairwell") {
                        return store[i].id
                    }
                }

                return null
            } else {
                return null
            }
        },

        residenceId() {
            const store = this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            if (store && store.length > 0) {
                return store.find(
                    (element) => element.routeName === "Residence"
                ).id
            } else {
                return null
            }
        },

        canBeMoved() {
            return (
                this.$route.params.action !== "create" &&
                !this.isSimplifiedResidence
            )
        },
    },

    async created() {
        if (!this.stairwellId && this.$route.params.action === "create") {
            toast.warning(this.$t("navigation.errors.notAccessible"))
            return this.$router.go(-1)
        }

        this.loadFloorOptions()
        this.loadBoxOptions()
        this.loadTypeOptions()

        if (this.$route.params.action === "edit") {
            await this.loadApartment()
        } else {
            this.locativeChoice = this.isLocative
            this.liberalChoice = this.isLiberal
        }
    },

    mixins: [v1mixin],

    methods: {
        loadFloorOptions() {
            this.floorOptions = [
                {
                    value: -3,
                    name: this.$t(
                        "apartmentsList.createOrEdit.floorOptions.subfloor_3"
                    ),
                },
                {
                    value: -2,
                    name: this.$t(
                        "apartmentsList.createOrEdit.floorOptions.subfloor_2"
                    ),
                },
                {
                    value: -1,
                    name: this.$t(
                        "apartmentsList.createOrEdit.floorOptions.subfloor_1"
                    ),
                },
            ]

            for (let i = 0; i < 31; i++) {
                this.floorOptions.push({
                    value: i,
                    name:
                        i === 0
                            ? this.$t(
                                  "apartmentsList.createOrEdit.floorOptions.floor_0"
                              )
                            : this.$t(
                                  "apartmentsList.createOrEdit.floorOptions.floor_?",
                                  {
                                      count: i,
                                  }
                              ),
                })
            }

            this.floor = this.floorOptions.find((option) => option.value === 0)
        },

        loadBoxOptions() {
            for (let i = 0; i < 251; i++) {
                this.boxOptions.push({
                    value: i,
                    name:
                        i === 0
                            ? this.$t(
                                  "apartmentsList.createOrEdit.boxOptions.nothing"
                              )
                            : this.$t(
                                  "apartmentsList.createOrEdit.boxOptions.box_?",
                                  {
                                      count: i,
                                  }
                              ),
                })
            }
            this.box = this.boxOptions.find((option) => option.value === 0)
        },

        loadTypeOptions() {
            for (let i = 0; i < 5; i++) {
                this.typeOptions.push({
                    value: i,
                    name:
                        i === 0
                            ? this.$t(
                                  "apartmentsList.createOrEdit.typeOptions.nothing"
                              )
                            : this.$t(
                                  "apartmentsList.createOrEdit.typeOptions.type_?",
                                  {
                                      count: i,
                                  }
                              ),
                })
            }

            this.type = this.typeOptions[0]
        },

        async loadApartment() {
            this.loading = true

            await getApartment({
                id: this.$route.params.id,
            }).then((response) => {
                if (response.state === "ok") {
                    this.initialData = response.data

                    this.name = response.data.nom
                    this.floor = this.floorOptions.find(
                        (option) => option.value == response.data.etage
                    )
                    this.box = this.boxOptions.find(
                        (option) => option.value == response.data.bal
                    )
                    this.type = this.typeOptions.find(
                        (option) => option.value == response.data.type
                    )
                    this.comment = response.data.comments
                    this.locativeChoice = response.data.loc == 1
                    this.liberalChoice = response.data.liberale == 1
                    this.liberalRangeId = response.data.idplgliberale
                }
            })

            this.loading = false
        },

        async createApartment(callBack = null) {
            this.loading = true

            await createApartment({
                nom: this.name,
                idcage: this.stairwellId,
                etage: this.floor.value,
                bal: this.box.value,
                type: this.type.value,
                comments: this.comment,
                loc: this.locativeChoice ? 1 : 0,
                liberale: this.liberalChoice ? 1 : 0,
            })
                .then((response) => {
                    if (response.state === "ok" && !callBack) {
                        //Delete ApartmentCreateOrEdit from breadCrumb to don't have it twice
                        const data = JSON.parse(JSON.stringify(this.breadCrumb))
                        data.forEach((element) => {
                            if (element.routeName === "ApartmentCreateOrEdit") {
                                data.splice(data.indexOf(element), 1)
                            }
                        })
                        this.$store.commit(
                            "breadCrumb/setClassicBreadCrumb",
                            data
                        )

                        this.$router.push({
                            name: "Apartment",
                            params: {
                                id: response.data.id,
                            },
                        })
                    } else if (response.state === "ok" && callBack) {
                        this.liberalRangeId = response.data.idplgliberale
                        this.appartmentId = response.data.id
                        callBack(this.appartmentId)
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },

        async editApartment(callBack = null) {
            this.loading = true

            await updateApartment({
                id: this.$route.params.id,
                nom: this.name,
                etage: this.floor.value,
                bal: this.box.value,
                type: this.type.value,
                comments: this.comment,
                loc: this.locativeChoice ? 1 : 0,
                liberale: this.liberalChoice ? 1 : 0,
            }).then((response) => {
                if (response.state === "ok" && !callBack) {
                    this.$router.push({
                        name: "Apartment",
                        params: {
                            id: this.$route.params.id,
                        },
                    })
                } else if (response.state === "ok" && callBack) {
                    this.liberalRangeId = response.data.idplgliberale
                    callBack()
                }
            })

            this.loading = false
        },

        async eraseApartment() {
            this.openModal("validation", {
                title: "apartment.delete",
                question: this.$t("apartmentsList.createOrEdit.deleteMessage", {
                    name: this.name,
                }),
                validateButton: this.$t("button.delete"),
                valid: async () => {
                    this.loading = true

                    await deleteApartment({
                        id: this.$route.params.id,
                    }).then((response) => {
                        if (response.state === "ok") {
                            const apartmentIndex = this.breadCrumb.findIndex(
                                (item) => item.routeName === "Apartment"
                            )

                            if (apartmentIndex >= 0) {
                                this.$router.push({
                                    name: this.breadCrumb[apartmentIndex - 1]
                                        .routeName,
                                    params: {
                                        id: this.breadCrumb[apartmentIndex - 1]
                                            .id,
                                    },
                                })
                            } else {
                                this.$router.push(
                                    `${
                                        this.breadCrumb[
                                            this.breadCrumb.length - 2
                                        ].link
                                    }`
                                )
                            }
                        }
                    })
                },
            })
        },

        async moveToApartment() {
            const choices = []
            let data

            await getStairwellList({
                idres: this.residenceId,
                limit: 1000,
            }).then((result) => {
                data = result.data.list
            })

            for (let i = 0; i < data.length; i++) {
                if (data[i].type === "CAGE") {
                    let stairwell = {
                        name: data[i].nom,
                        id: data[i].id,
                    }

                    if (data[i].id !== this.stairwellId) {
                        choices.push(stairwell)
                    }
                }
            }

            this.openModal("move-to", {
                title: "moveTo",
                question: this.$t("apartmentsList.createOrEdit.moveToMessage"),
                choices: choices,
                validateButton: this.$t("button.move"),
                valid: async (data) => {
                    await updateApartment({
                        id: this.$route.params.id,
                        nom: this.name,
                        idcage: data.id,
                    }).then((response) => {
                        if (response.state === "ok") {
                            const newStairwell = {
                                name: data.name,
                                link: "/stairwell/" + data.id,
                                routeName: "Stairwell",
                                id: data.id,
                            }

                            this.$store.commit(
                                "breadCrumb/updateSepecificPartClassicBreadCrumb",
                                newStairwell
                            )

                            this.$router.push({
                                name: "Apartment",
                                params: {
                                    id: this.$route.params.id,
                                },
                            })
                        }
                    })
                },
            })
        },

        submitAction() {
            if (this.$route.params.action === "create") {
                this.createApartment()
            } else {
                this.editApartment()
            }
        },

        goBack() {
            this.$router.go(-1)
        },

        async goTo(route) {
            let callBack

            switch (route) {
                case "DisplayHours":
                    callBack = () => {
                        this.goToV1View(
                            "data/plage/&ID=" +
                                this.liberalRangeId +
                                "&id_appart=" +
                                this.appartmentId
                        )
                    }
                    break

                case "LocativeGestion":
                    callBack = (apartId) => {
                        this.goToV1View(
                            `data/location/type=1&flat_id=${apartId}`
                        )
                    }
                    break
            }

            if (this.name) {
                this.title === this.$t("apartmentsList.create")
                    ? await this.createApartment(callBack)
                    : await this.editApartment(callBack)
            }
        },

        liberalInfos() {
            this.openModal("alert", {
                title: "liberal_infos",
                question: this.$t(
                    "apartmentsList.createOrEdit.liberalDescription"
                ),
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";
@import "@/assets/scss/_loader.scss";
p {
    margin: 0;
}
.apartement-main-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 3em;
    padding: 20px 80px 40px 80px;

    @media all and (max-width: 1024px) {
        padding: 1em 0 1em 1em;
        overflow-y: auto;
    }

    .header-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.5em;

        h1 {
            font-size: $veryBig;
            font-family: $font_avenir_black;
            margin: 0;
        }

        h2 {
            font-size: $semiNormal;
            font-family: $font_avenir_book;
            margin: 0;
        }
    }

    .content-container {
        width: 100%;
        display: flex;
        // gap: 1em;

        @media all and (min-width: 1024px) {
            overflow-y: auto;
        }

        .left-part-container {
            width: 100%;
            max-width: 550px;
            display: flex;
            flex-direction: column;
            gap: 1em;
            overflow: auto;
            padding-right: 3em;

            @media all and (max-width: 1024px) {
                padding-right: 1em;
            }

            .input-wrapper {
                display: flex;
                gap: 0.5em;
                align-items: start;

                @media all and (max-width: 639px) {
                    flex-direction: column;
                }

                p {
                    min-width: max-content;
                    width: 100%;
                    max-width: 150px;
                }

                .input {
                    width: 100%;
                    max-width: 390px;
                }
            }

            .checkbox-container {
                display: flex;
                justify-content: space-between;

                @media all and (max-width: 639px) {
                    max-width: 390px;
                }

                .checkbox-wrapper {
                    display: flex;
                    gap: 1em;
                    align-items: center;
                    display: flex;
                    align-items: center;
                    padding-left: 4px;

                    .checkbox {
                        width: 15px;
                        height: 15px;
                        margin: 0 !important;
                    }

                    p {
                        display: flex;
                        align-items: start;
                        gap: 0.4em;

                        i {
                            font-size: 10px;

                            &:hover {
                                cursor: pointer;
                            }
                        }
                    }
                }

                .display-hours {
                    &:hover {
                        cursor: pointer;
                    }

                    p {
                        text-decoration: underline;
                        text-align: end;
                    }
                }
            }

            .button-wrapper {
                display: flex;
                gap: 1em;
                flex-wrap: wrap;

                @media all and (max-width: 1024px) {
                    justify-content: center;
                }
            }
        }

        .separator-line {
            width: 1px;
            height: 100%;
            background-color: $blue-neutral;

            @media all and (max-width: 1024px) {
                display: none;
            }
        }

        .right-part-container {
            width: max-content;
            display: flex;
            justify-content: start;
            padding-left: 3em;

            @media all and (max-width: 1024px) {
                display: none;
            }

            .image-wrapper {
                width: max-content;

                img {
                    width: 200px;
                    height: 100%;
                    object-fit: contain;
                    max-height: 250px;
                }
            }
        }
    }
}

.loader {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
